import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Box, Typography, Button, FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { Plus, Edit, Trash2, ExternalLink } from 'lucide-react';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { fetchSites } from 'store/sitesSlice';
import {
  fetchSiteSocials,
  setParams,
  createSiteSocial,
  deleteSiteSocial,
  updateSiteSocial,
} from 'store/siteSocialsSlice';
import { RootState } from 'store/index';
import { commonGridStyles } from 'styles/gridStyles';
import styles from './SiteSocials.module.scss';
import { Alert } from 'components/alert/alert';
import ConfirmModal from 'components/modals/ConfirmModal';
import SiteSocialModal from './SiteSocialModal';
import { SiteSocial } from 'types/siteSocial';
import commonStyles from 'styles/common.module.scss';

const SiteSocials: React.FC = () => {
  const dispatch = useAppDispatch();
  const { data: sites, isLoading: sitesLoading } = useAppSelector(
    (state: RootState) => state.sites,
  );
  const {
    data: socials,
    isLoading,
    params,
    isCreating,
  } = useAppSelector((state: RootState) => state.siteSocials);

  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingSocial, setEditingSocial] = useState<SiteSocial | null>(null);
  const [modalError, setModalError] = useState<string | null>(null);
  const [feedback, setFeedback] = useState<{ message: string; type: 'success' | 'error' } | null>(
    null,
  );
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const showFeedback = useCallback((message: string, type: 'success' | 'error') => {
    setFeedback({ message, type });
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => setFeedback(null), 3000);
  }, []);

  useEffect(() => {
    if (sites.length === 0) {
      dispatch(fetchSites({ page: 1, limit: 100, sortField: 'id', sortValue: 'ASC' }));
    }
  }, [dispatch, sites.length]);

  useEffect(() => {
    if (sites.length > 0 && isInitialLoad && params.siteId === '') {
      const dgentaSite = sites.find((s) => s.title.toLowerCase().includes('dgenta'));
      if (dgentaSite) {
        dispatch(setParams({ siteId: dgentaSite.id.toString() }));
        setIsInitialLoad(false);
      }
    }
  }, [sites, isInitialLoad, params.siteId, dispatch]);

  useEffect(() => {
    if (params.siteId) {
      dispatch(fetchSiteSocials(params));
    }
  }, [dispatch, params]);

  const handleSiteChange = (siteId: string | number) => {
    dispatch(setParams({ siteId: siteId === 'all' ? undefined : siteId }));
  };

  const handleModalSubmit = async (data: any, imageFile?: File) => {
    setModalError(null);
    try {
      if (editingSocial) {
        const resultAction = await dispatch(
          updateSiteSocial({ id: editingSocial.id, data, image: imageFile }),
        );
        if (updateSiteSocial.fulfilled.match(resultAction)) {
          showFeedback('Social link updated successfully', 'success');
          setIsModalOpen(false);
        } else {
          setModalError((resultAction.payload as string) || 'Failed to update social link');
        }
      } else {
        const resultAction = await dispatch(createSiteSocial({ ...data, image: imageFile }));
        if (createSiteSocial.fulfilled.match(resultAction)) {
          showFeedback('Social link created successfully', 'success');
          setIsModalOpen(false);
        } else {
          setModalError((resultAction.payload as string) || 'Failed to create social link');
        }
      }
    } catch (error) {
      setModalError('An unexpected error occurred');
    }
  };

  const handleDeleteConfirm = async () => {
    if (deleteId) {
      try {
        const resultAction = await dispatch(deleteSiteSocial(deleteId));
        if (deleteSiteSocial.fulfilled.match(resultAction)) {
          showFeedback('Social link deleted successfully', 'success');
        } else {
          showFeedback((resultAction.payload as string) || 'Failed to delete social link', 'error');
        }
      } catch (err) {
        showFeedback('An unexpected error occurred', 'error');
      }
      setDeleteId(null);
    }
  };

  const columns: GridColDef[] = [
    { field: 'id', headerName: 'ID', width: 70, align: 'center', headerAlign: 'center' },
    {
      field: 'imageUrl',
      headerName: 'Icon',
      width: 100,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => (
        <Box
          sx={{ display: 'flex', alignItems: 'center', height: '100%', justifyContent: 'center' }}
        >
          {params.value ? (
            <img src={params.value} alt="icon" className={styles.iconPreview} />
          ) : (
            <Box
              className={styles.iconPreview}
              sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            >
              <Typography variant="caption" sx={{ color: 'var(--text-muted)' }}>
                No icon
              </Typography>
            </Box>
          )}
        </Box>
      ),
    },
    { field: 'name', headerName: 'Name', width: 150, align: 'left', headerAlign: 'left' },
    // { field: 'sortOrder', headerName: 'Order', width: 80, align: 'center', headerAlign: 'center' },
    {
      field: 'socialLink',
      headerName: 'Social Link',
      width: 350,
      align: 'left',
      headerAlign: 'left',
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <a href={params.value} target="_blank" rel="noopener noreferrer" className={styles.url}>
            {params.value}
          </a>
          <ExternalLink size={12} color="var(--accent-blue)" />
        </Box>
      ),
    },
    {
      field: 'actions',
      headerName: 'Action',
      width: 150,
      sortable: false,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => (
        <Box className={styles.actions}>
          <Button
            size="small"
            className={styles.editBtn}
            onClick={() => {
              setEditingSocial(params.row);
              setIsModalOpen(true);
            }}
          >
            <Edit size={14} />
          </Button>
          <Button
            size="small"
            className={styles.deleteBtn}
            onClick={() => setDeleteId(params.row.id)}
          >
            <Trash2 size={14} />
          </Button>
        </Box>
      ),
    },
  ];

  return (
    <Box className={styles.socialsContainer}>
      <Box className={styles.header}>
        <Typography variant="h4" className={styles.title}>
          Social Links
        </Typography>
        <Box className={styles.controls}>
          <Box className={styles.filterItem}>
            <FormControl size="small" className={styles.selectContainer}>
              <InputLabel sx={{ color: '#8698a6' }}>Site</InputLabel>
              <Select
                value={params.siteId || 'all'}
                label="Site"
                onChange={(e) => handleSiteChange(e.target.value as string | number)}
                disabled={sitesLoading}
              >
                <MenuItem value="all">All Sites</MenuItem>
                {sites.map((site) => (
                  <MenuItem key={site.id} value={site.id}>
                    {site.title}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          <Button
            variant="contained"
            className={styles.createBtn}
            startIcon={<Plus size={18} />}
            onClick={() => {
              setEditingSocial(null);
              setIsModalOpen(true);
            }}
          >
            Social Link
          </Button>
        </Box>
      </Box>

      <Alert message={feedback?.message} type={feedback?.type} />
      <Alert message={modalError} type="error" />

      <Box className={commonStyles.gridWrapper}>
        <DataGrid
          rows={socials}
          loading={isLoading}
          columns={columns}
          sx={{
            ...commonGridStyles,
            mt: 6,
            mb: 6,
          }}
        />
      </Box>

      {/* Modals */}
      <ConfirmModal
        open={!!deleteId}
        title="Delete Social Link"
        description="Are you sure you want to delete this social link? This action cannot be undone."
        onClose={() => setDeleteId(null)}
        onConfirm={handleDeleteConfirm}
        confirmText="Delete"
        isDanger
      />

      <SiteSocialModal
        open={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setEditingSocial(null);
          setModalError(null);
        }}
        onSubmit={handleModalSubmit}
        sites={sites}
        isLoading={isCreating}
        initialData={editingSocial}
        // error={modalError}
      />
    </Box>
  );
};

export default SiteSocials;
